import React from "react";
import { MenuItem, SubMenu } from "react-pro-sidebar";
import { NavLink, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaUsers, FaLocationDot } from "react-icons/fa6";
import { MdOutlinePayment, MdTour, MdSettings, MdSportsBaseball, MdAddBox } from "react-icons/md";
import { RiTeamFill } from "react-icons/ri";
import { IoTrophy } from "react-icons/io5";
import { FaThList, FaTag, FaShieldAlt, FaClipboardList, FaUserTie } from "react-icons/fa";
import { AiOutlineDashboard } from "react-icons/ai";
import { BiCategoryAlt } from "react-icons/bi";
import { BsBell } from "react-icons/bs";
import { GiTargetArrows } from "react-icons/gi"; // Draft icon
import { FiPackage } from "react-icons/fi"; // Packages icon
import { HiUserGroup } from "react-icons/hi"; // Registrations icon
import "../../assets/css/admin-menu.css";

/** Small uppercase group heading; a thin divider when the sidebar is collapsed. */
const Section = ({ label, isCollapsed }) =>
  isCollapsed ? (
    <li className="menu-section-divider" role="presentation" />
  ) : (
    <li className="menu-section-label" role="presentation">
      {label}
    </li>
  );

/** Label with an optional count badge (badge hidden when collapsed). */
const Label = ({ text, badge, tone = "accent", isCollapsed }) => {
  if (isCollapsed) return null;
  return (
    <span className="menu-label">
      <span>{text}</span>
      {badge ? <span className={`menu-badge menu-badge-${tone}`}>{badge}</span> : null}
    </span>
  );
};

const AdminMenu = ({ isCollapsed }) => {
  // Badges come from the admin dashboard summary (already filtered by the selected sport).
  const dashboard = useSelector((state) => state.dashboard?.data);
  const balancesDue = dashboard?.kpis?.teamsWithBalance || 0;
  const cancellations = (dashboard?.alerts || []).find((a) => a.type === "cancellations")?.count || 0;

  return (
    <>
      {/* ── OVERVIEW ─────────────────────────────────────────────────────── */}
      <Section label="Overview" isCollapsed={isCollapsed} />
      <NavLink to="/dashboard" end className="menu-item-link text-white">
        <MenuItem icon={<AiOutlineDashboard className="menu-icon" />}>{!isCollapsed && "Dashboard"}</MenuItem>
      </NavLink>

      {/* ── EVENTS ───────────────────────────────────────────────────────── */}
      <Section label="Events" isCollapsed={isCollapsed} />
      <SubMenu label={<Label text="Tournaments" isCollapsed={isCollapsed} />} icon={<MdTour className="menu-icon" />}>
        <NavLink to="/dashboard/alltournaments" className="menu-item-link text-white">
          <MenuItem>{isCollapsed ? <FaThList className="menu-icon" /> : "All Tournaments"}</MenuItem>
        </NavLink>
        <NavLink to="/dashboard/addtournaments" className="menu-item-link text-white">
          <MenuItem>{isCollapsed ? <MdAddBox className="menu-icon" /> : "Add Tournament"}</MenuItem>
        </NavLink>
      </SubMenu>

      <SubMenu
        label={<Label text="Draft Tournaments" badge={cancellations || null} isCollapsed={isCollapsed} />}
        icon={<GiTargetArrows className="menu-icon" />}
      >
        <NavLink to="/dashboard/draft/packages" className="menu-item-link text-white">
          <MenuItem>{isCollapsed ? <FiPackage className="menu-icon" /> : "Manage Packages"}</MenuItem>
        </NavLink>
        <NavLink to="/dashboard/draft/registrations" className="menu-item-link text-white">
          <MenuItem>{isCollapsed ? <HiUserGroup className="menu-icon" /> : "Player Registrations"}</MenuItem>
        </NavLink>
        <NavLink to="/dashboard/draft/cancellations" className="menu-item-link text-white">
          <MenuItem>
            {isCollapsed ? (
              <FaThList className="menu-icon" />
            ) : (
              <Label text="Cancellation Requests" badge={cancellations || null} isCollapsed={isCollapsed} />
            )}
          </MenuItem>
        </NavLink>
      </SubMenu>

      <SubMenu label={<Label text="Divisions" isCollapsed={isCollapsed} />} icon={<IoTrophy className="menu-icon" />}>
        <NavLink to="/dashboard/alldivisions" className="menu-item-link text-white">
          <MenuItem>{isCollapsed ? <FaThList className="menu-icon" /> : "All Divisions"}</MenuItem>
        </NavLink>
        <NavLink to="/dashboard/addivision" className="menu-item-link text-white">
          <MenuItem>{isCollapsed ? <MdAddBox className="menu-icon" /> : "Add Division"}</MenuItem>
        </NavLink>
      </SubMenu>

      <SubMenu label={<Label text="Venues" isCollapsed={isCollapsed} />} icon={<FaLocationDot className="menu-icon" />}>
        <NavLink to="/dashboard/allvenue" className="menu-item-link text-white">
          <MenuItem>{isCollapsed ? <FaThList className="menu-icon" /> : "All Venues"}</MenuItem>
        </NavLink>
        <NavLink to="/dashboard/addvenue" className="menu-item-link text-white">
          <MenuItem>{isCollapsed ? <MdAddBox className="menu-icon" /> : "Add Venue"}</MenuItem>
        </NavLink>
      </SubMenu>

      {/* ── PEOPLE ───────────────────────────────────────────────────────── */}
      <Section label="People" isCollapsed={isCollapsed} />
      <SubMenu label={<Label text="Teams" isCollapsed={isCollapsed} />} icon={<RiTeamFill className="menu-icon" />}>
        <NavLink to="/dashboard/allteams" className="menu-item-link text-white">
          <MenuItem>{isCollapsed ? <FaThList className="menu-icon" /> : "All Teams"}</MenuItem>
        </NavLink>
        <NavLink to="/dashboard/addteams" className="menu-item-link text-white">
          <MenuItem>{isCollapsed ? <MdAddBox className="menu-icon" /> : "Add Team"}</MenuItem>
        </NavLink>
      </SubMenu>

      <SubMenu label={<Label text="Members" isCollapsed={isCollapsed} />} icon={<FaUsers className="menu-icon" />}>
        <NavLink to="/dashboard/allpersons" className="menu-item-link text-white">
          <MenuItem>{isCollapsed ? <FaThList className="menu-icon" /> : "All Members"}</MenuItem>
        </NavLink>
        <NavLink to="/dashboard/addperson" className="menu-item-link text-white">
          <MenuItem>{isCollapsed ? <MdAddBox className="menu-icon" /> : "Add Member"}</MenuItem>
        </NavLink>
      </SubMenu>

      {/* ── MONEY ────────────────────────────────────────────────────────── */}
      <Section label="Money" isCollapsed={isCollapsed} />
      <SubMenu
        label={<Label text="Payments" badge={balancesDue ? `${balancesDue} due` : null} tone="amber" isCollapsed={isCollapsed} />}
        icon={<MdOutlinePayment className="menu-icon" />}
      >
        <NavLink to="/dashboard/payment/history" className="menu-item-link text-white">
          <MenuItem>{isCollapsed ? <FaThList className="menu-icon" /> : "Payments History"}</MenuItem>
        </NavLink>
      </SubMenu>

      <SubMenu label={<Label text="Promotions" isCollapsed={isCollapsed} />} icon={<FaTag className="menu-icon" />}>
        <NavLink to="/dashboard/coupons" className="menu-item-link text-white">
          <MenuItem>{isCollapsed ? <FaTag className="menu-icon" /> : "Coupon Manager"}</MenuItem>
        </NavLink>
        <NavLink to="/dashboard/tournament-rules" className="menu-item-link text-white">
          <MenuItem>{isCollapsed ? <FaShieldAlt className="menu-icon" /> : "Rules Manager"}</MenuItem>
        </NavLink>
      </SubMenu>

      {/* ── SETUP ────────────────────────────────────────────────────────── */}
      <Section label="Setup" isCollapsed={isCollapsed} />
      <SubMenu label={<Label text="Basic Tables" isCollapsed={isCollapsed} />} icon={<MdSettings className="menu-icon" />}>
        <SubMenu label={!isCollapsed && "Sport"} icon={<MdSportsBaseball className="menu-icon" />}>
          <NavLink to="/dashboard/allsports" className="menu-item-link text-white">
            <MenuItem>{isCollapsed ? <FaThList className="menu-icon" /> : "All Sports"}</MenuItem>
          </NavLink>
          <NavLink to="/dashboard/addsport" className="menu-item-link text-white">
            <MenuItem>{isCollapsed ? <MdAddBox className="menu-icon" /> : "Add Sport"}</MenuItem>
          </NavLink>
        </SubMenu>

        <SubMenu label={!isCollapsed && "Division Master"} icon={<BiCategoryAlt className="menu-icon" />}>
          <NavLink to="/dashboard/alldivisionmasters" className="menu-item-link text-white">
            <MenuItem>{isCollapsed ? <FaThList className="menu-icon" /> : "All Division Masters"}</MenuItem>
          </NavLink>
          <NavLink to="/dashboard/adddivisionmaster" className="menu-item-link text-white">
            <MenuItem>{isCollapsed ? <MdAddBox className="menu-icon" /> : "Add Division Master"}</MenuItem>
          </NavLink>
        </SubMenu>

        {/* Directors — master table; tournaments can have N directors */}
        <NavLink to="/dashboard/directors" className="menu-item-link text-white">
          <MenuItem icon={<FaUserTie className="menu-icon" />}>{!isCollapsed && "Directors"}</MenuItem>
        </NavLink>

        {/* Rules Templates — edit default content per type */}
        <NavLink to="/dashboard/rules-templates" className="menu-item-link text-white">
          <MenuItem icon={<FaClipboardList className="menu-icon" />}>{!isCollapsed && "Rules Templates"}</MenuItem>
        </NavLink>

        <MenuItem icon={<BsBell />} routerLink={<Link to="/dashboard/alert-settings" />}>
          {!isCollapsed && "Alert Settings"}
        </MenuItem>
      </SubMenu>
    </>
  );
};

export default AdminMenu;
